/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Custom agents", description = "Create specialized agents with saved prompts scoped to specific teams, metrics, or use cases. Custom agents can be used in chats and reports.", lastUpdatedDate = new Date(1779408000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Custom agents",
        description: "Create specialized agents with saved prompts scoped to specific teams, metrics, or use cases. Custom agents can be used in chats and reports.",
        lastUpdatedDate: "Fri May 22 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "When to use a custom agent",
          "depth": 0,
          "id": "when-to-use-a-custom-agent"
        }, {
          "value": "Create a custom agent",
          "depth": 0,
          "id": "create-a-custom-agent"
        }, {
          "value": "Use a custom agent in chat",
          "depth": 0,
          "id": "use-a-custom-agent-in-chat"
        }, {
          "value": "Use a custom agent in reports",
          "depth": 0,
          "id": "use-a-custom-agent-in-reports"
        }, {
          "value": "Example custom agents",
          "depth": 0,
          "id": "example-custom-agents"
        }, {
          "value": "Manage custom agents",
          "depth": 0,
          "id": "manage-custom-agents"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ul: "ul",
      li: "li",
      ol: "ol",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams, platform admins"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Custom agents are saved prompts that define a specific task, set of instructions, or analysis framework. Use them in chats and reports to get consistent, tailored results."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "A custom agent is a reusable set of instructions that tells the agent how to behave, what data to focus on, and how to format its responses. Think of it as a specialist you can call on for a particular domain, like paid media analysis, creative performance, lifecycle reporting, or any other area your team works in."
      }), "\n", _jsx(_components.p, {
        children: "Once created, custom agents can be selected in chats and applied to reports, so the same expertise is available across your team."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Custom agents are available to all users in your workspace. Any user with access to Agents can select and use a custom agent, though the data each agent can query depends on the user's parent model permissions."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/custom-agents/custom-agents-list.png",
          alt: "Custom agents list"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "when-to-use-a-custom-agent",
        children: "When to use a custom agent"
      }), "\n", _jsx(_components.p, {
        children: "Use a custom agent when you want to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Standardize analysis"
          }), " — ensure the same metrics, definitions, and formatting are used consistently across your team. For example, a \"Weekly Paid Media Analyst\" agent that always reports on spend, CPA, CTR, ROAS, and creative fatigue by campaign objective."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Encode metric definitions"
          }), " — embed your organization's specific definitions (like how you calculate LTV or attribution windows) directly into the agent's instructions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Scope to a domain"
          }), " — focus the agent on a specific data set or marketing channel so it doesn't need to be told the context every time."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Share expertise"
          }), " — let team members who are less familiar with the data access the same quality of analysis by selecting a pre-configured agent."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-a-custom-agent",
        children: "Create a custom agent"
      }), "\n", _jsxs(_components.p, {
        children: ["To create a custom agent, go to ", _jsx(_components.strong, {
          children: "Agents > Custom agents"
        }), " and click ", _jsx(_components.strong, {
          children: "Create custom agent"
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Name"
            }), " — give the agent a descriptive name that identifies its task or purpose. For example, \"Paid Media Analyst\", \"Creative Trend Analysis\", or \"Brand & Creative Review\"."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Instructions"
            }), " — describe what the agent should do, what data it should focus on, and how it should format responses. This is the core prompt that shapes the agent's behavior."]
          }), "\n", _jsx(_components.p, {
            children: "Good instructions typically include:"
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "The agent's task and area of focus"
            }), "\n", _jsx(_components.li, {
              children: "Which metrics and KPIs to prioritize"
            }), "\n", _jsx(_components.li, {
              children: "How to structure the output (tables, charts, summaries)"
            }), "\n", _jsx(_components.li, {
              children: "Any definitions, thresholds, or business rules to follow"
            }), "\n", _jsx(_components.li, {
              children: "Tone and formatting preferences"
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click ", _jsx(_components.strong, {
              children: "Create"
            }), " to save the custom agent."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/custom-agents/create-custom-agent.png",
          alt: "Create custom agent"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Use chat to develop your prompt."
          }), " Before creating a custom agent, use a regular chat to iterate on the instructions. Ask the agent to analyze your data, refine the output, and adjust the formatting until you're happy with the results. Then save the final prompt as a custom agent."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-a-custom-agent-in-chat",
        children: "Use a custom agent in chat"
      }), "\n", _jsx(_components.p, {
        children: "There are two ways to use a custom agent in a chat:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Select it when starting a chat"
          }), " — choose the custom agent from the agent selector in the chat input before typing your first message. The entire conversation follows the custom agent's instructions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use the \"Use in chat\" button"
          }), " — from the custom agent's detail view, click ", _jsx(_components.strong, {
            children: "Use in chat"
          }), " to open a new chat with that agent already selected."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "When a custom agent is active, its instructions are applied alongside the data context from the selected parent model. You can still ask any question — the custom agent shapes how the agent responds, not what it can access."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/custom-agents/search-custom-agents.png",
          alt: "Search custom agents"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-a-custom-agent-in-reports",
        children: "Use a custom agent in reports"
      }), "\n", _jsx(_components.p, {
        children: "Custom agents can be applied to reports to ensure consistent formatting and analysis across scheduled runs. When creating or editing a report, select a custom agent to scope the report's behavior."
      }), "\n", _jsxs(_components.p, {
        children: ["This is useful for standardized weekly or daily reports where you want the same structure, metrics, and tone every time. See ", _jsx(_components.a, {
          href: "/agents/reports",
          children: "Reports"
        }), " for more on creating and scheduling reports."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/custom-agents/custom-agent-report.png",
          alt: "Create report with custom agent"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "example-custom-agents",
        children: "Example custom agents"
      }), "\n", _jsx(_components.p, {
        children: "Here are some common patterns for custom agents:"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Paid Media Analyst"
        }), " — analyzes ad performance across Meta, Google Ads, and TikTok. Reports on spend, CPA, CTR, ROAS, and creative fatigue by campaign objective. Flags ads with declining performance week-over-week."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Lifecycle Performance Analyst"
        }), " — focused on email, SMS, and push campaign performance. Reports on send volume, open rate, click rate, and conversion rate by campaign type. Compares promotional vs. triggered campaigns."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Brand & Creative Review"
        }), " — reviews all email and ad copy before it goes to customers. Checks for compliance with brand standards, voice and tone guidelines, and legal requirements."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Competitor Analysis"
        }), " — monitors competitor ad activity and creative trends. Analyzes competitor messaging, offers, and creative formats to surface opportunities."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Weekly Executive Briefing"
        }), " — produces an insight-driven briefing on overall business and marketing performance. Summarizes KPIs, flags anomalies, and recommends actions."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "manage-custom-agents",
        children: "Manage custom agents"
      }), "\n", _jsx(_components.p, {
        children: "The Custom agents list shows all agents in your workspace with their name and last updated date."
      }), "\n", _jsxs(_components.p, {
        children: ["Click the ", _jsx(_components.strong, {
          children: "..."
        }), " menu on any custom agent to edit or delete it. Editing a custom agent updates its instructions for all future chats and report runs. Past results are not affected."]
      }), "\n", _jsx(_components.p, {
        children: "Custom agents are scoped to parent models and respect user-level permissions. Users can only access custom agents associated with parent models they have access to."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/custom-agents/edit-custom-agent.png",
          alt: "Edit custom agent"
        })
      })]
    });
  }
}
export default MDXContent;
